import { Container, Graphics, Point, Text, TextStyle } from 'pixi.js';
import { AssetManager, SpineExtended, container, events } from '@game-studio/arcade-base';
import { gameSettings } from '../../../models/game-settings';
import { gameModel } from '../../../models/game-model';
import { CollectorState, collectorService } from '../../../services/collector-service';
import gsap from 'gsap';
import { ISkeletonData } from 'pixi-spine';
import { BuildType, ICollectorPanel } from './collector-view';
import { optimizationService } from '@/src/core/services/optimization-service';
import { ContextStateSubscriber } from '@/src/core/services/context-state-subscriber';
import { soundService } from '@/src/core/services/sound-service';
import { gameEventEmitter } from '@/src/events';

export class PanelLandscapeView extends Container {
    private _prizePanelConfig: ICollectorPanel;

    private _cellViews: SpineExtended[];
    private _cellPositions: Point[];

    private _valueText: Text;
    private _boundBox: Graphics;

    private _cellStates: number[];
    private _oneToGo: boolean;
    private _win: boolean;

    constructor(prizePanelConfig: ICollectorPanel) {
        super();
        this._prizePanelConfig = prizePanelConfig;
        this.buildCollector(prizePanelConfig);
        this.buildValueLabel();
        this.addListeners();
    }

    // Hide elements before drawing.
    public preDraw(): void {
        for (let $cellIndex = 0; $cellIndex < this._cellViews.length; $cellIndex++) {
            this._cellViews[$cellIndex].visible = false;
        }
    }

    // Show elements after drawing.
    public postDraw(): void {
        for (let $cellIndex = 0; $cellIndex < this._cellViews.length; $cellIndex++) {
            this._cellViews[$cellIndex].visible = true;
        }
    }

    private buildCollector(prizePanelConfig: ICollectorPanel): void {
        this._cellViews = [];
        this._cellPositions = [];

        for (let $cellIndex = 0; $cellIndex < prizePanelConfig.cells; $cellIndex++) {
            // Create spine asset.
            const spineData: ISkeletonData = AssetManager.getSpineResourceData('collectors');
            const cell = new SpineExtended(spineData);
            this.addChild(cell);

            // Set up spine asset.
            cell.skeleton.setSkinByName(prizePanelConfig.key);
            cell.state.setAnimation(0, 'sizer', true);

            // Store in array.
            this._cellViews.unshift(cell);

            // Set local position.
            const spacingX = cell.width * 1.06;
            const spacingY = cell.height * 1.12;
            const centeringXoffset = spacingX * ((prizePanelConfig.cells - 1) * 0.5);

            // Populate cell postion array according to build type.
            let position: number;
            switch (prizePanelConfig.buildType) {
                case BuildType.TOP:
                    position = spacingX * $cellIndex - centeringXoffset;
                    this._cellPositions.unshift(new Point(position, 0));
                    break;

                default:
                    position = spacingY * $cellIndex;
                    this._cellPositions.unshift(new Point(0, position));
                    break;
            }
        }

        // Configure row collectors to collect from the outside in.
        if (prizePanelConfig.buildType === BuildType.TOP) {
            this._cellPositions = this.oscillateArray(this._cellPositions);
        }

        this.positionCellsAndBounds();
        this._cellStates = new Array(prizePanelConfig.cells).fill(CollectorState.Available);
    }

    private positionCellsAndBounds(): void {
        // Create bounding sizer container.
        this.initSizer();

        for (let $cellIndex = 0; $cellIndex < this._prizePanelConfig.cells; $cellIndex++) {
            const cell = this._cellViews[$cellIndex];
            const position = this._cellPositions[$cellIndex];
            // Position cell.
            cell.position.set(position.x, position.y);
            // Build sizer bounding.
            this.buildSizer(cell);
            // Set cell spine to idle state.
            cell.state.setAnimation(0, 'idle', true);
            // Add Optimization.
            optimizationService.optimizeOn(cell);
            ContextStateSubscriber.subscribeMethod(() => {this.onCollectorUpdated$()})
        }

        // End building the sizer.
        this._boundBox.endFill();
    }

    private initSizer(): void {
        this._boundBox = new Graphics();
        this.addChild(this._boundBox);
    }

    private buildSizer(referenceSpine: SpineExtended): void {
        this._boundBox.beginFill(0xffffff, 0);
        this._boundBox.drawRect(referenceSpine.x - referenceSpine.width / 2, referenceSpine.y - referenceSpine.height / 2, referenceSpine.width, referenceSpine.height);
    }

    private updateCells(collectorPanelState: CollectorState[]): void {
        for (let $panelStateIndex = 0; $panelStateIndex < collectorPanelState.length; $panelStateIndex++) {
            const modelState = collectorPanelState[$panelStateIndex];
            const cellState = this._cellStates[$panelStateIndex];
            const cellView = this._cellViews[$panelStateIndex];

            if (modelState === CollectorState.Allocated && cellState === CollectorState.Available) {
                this._cellStates[$panelStateIndex] = CollectorState.Allocated;

                optimizationService.optimizeOff(cellView);
                cellView.state.setAnimation(0, 'collected', false);
                cellView.setAnimationPromise('collected', { trackIndex: 1 }).then(() => {
                    optimizationService.optimizeOn(cellView);
                });
            }

            if (modelState === CollectorState.Available && !this._oneToGo) {
                optimizationService.runOptimizedSpine(cellView, () => {
                    cellView.state.setAnimation(0, 'idle', false);
                    cellView.state.setAnimation(1, 'idle', false);
                });
            }
        }
    }

    private handleOneToGo(collectorPanelState: CollectorState[]): void {
        if (this._oneToGo || !collectorService.isOneToGo(collectorPanelState)) {
            return;
        }
        this._oneToGo = true;

        gameModel.oneToGoStates[this._prizePanelConfig.id] = CollectorState.Allocated;
        soundService.checkOneToGoMusic();

        // Play one to go animation sequence.
        const cell = this._cellViews[this._cellViews.length - 1];
        optimizationService.optimizeOff(cell);
        cell.addAnimationPromise('oneToGo', { trackIndex: 1 }).then(() => {
            cell.state.setAnimation(1, 'oneToGoLoop', true);
        });
    }

    private handleWin(collectorPanelState: CollectorState[]): void {
        if (this._win || !collectorService.isWin(collectorPanelState)) {
            return;
        }
        gameModel.oneToGoStates[this._prizePanelConfig.id] = CollectorState.Available;
        soundService.checkOneToGoMusic();

        this._win = true;

        // Determine the type of win.
        const winType = gameModel.goldPrize === this._prizePanelConfig.id ? 'goldenWin' : 'win';

        soundService.playWinSound(this._prizePanelConfig.id);

        // Play win animation sequence.
        for (let $stateIndex = 0; $stateIndex < collectorPanelState.length; $stateIndex++) {
            gsap.delayedCall(0.2 * $stateIndex, () => {
                const cell = this._cellViews[$stateIndex];
                optimizationService.optimizeOff(cell);
                cell.setAnimationPromise(winType, { trackIndex: 1 }).then(() => {
                    cell.setAnimationPromise(winType + 'Loop', { trackIndex: 1, loop: true });
                });
            });
        }
    }

    private oscillateArray(inputArray: Point[]): Point[] {
        // Refactors the array to an inside out sequence: [1,2,3,4,5] => [1,5,2,4,3].
        const newArray = inputArray.reverse().map((value, index) => (index % 2 === 0 ? inputArray[index / 2] : inputArray[inputArray.length - (index + 1) / 2]));
        return newArray;
    }

    private buildValueLabel(): void {
        const style = new TextStyle({
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.6,
            dropShadowBlur: 4,
            dropShadowDistance: 1,
            fill: '#ffffff',
            fontFamily: 'Wallop-Reg',
            fontWeight: 'bold',
            fontSize: 23,
            lineJoin: 'round',
            miterLimit: 29,
            stroke: '#f5f5f5',
            whiteSpace: 'normal',
            wordWrap: true,
            wordWrapWidth: 200
        });

        this._valueText = new Text('£0000', style);
        this.addChild(this._valueText);
        this.updatePrizeValue$();
    }

    private onCollectorUpdated$(): void {
        const collectorPanelState = gameModel.collectorStates[this._prizePanelConfig.id];
        this.updateCells(collectorPanelState);
        this.handleOneToGo(collectorPanelState);
        this.handleWin(collectorPanelState);
    }

    private updatePrizeValue$(): void {
        const prizeValue: number = container.stake.currentStake * gameSettings.winMultipliers[this._prizePanelConfig.id + 1];
        const prizeText: string = container.currencyFormatter.precise(prizeValue);
        this._valueText.text = prizeText;
        this._valueText.x = -this._valueText.width * 0.5;
        this._valueText.y = -this._valueText.height * 0.5;
    }

    private onNewGame$(): void {
        // Reset local states.
        this._oneToGo = false;
        this._win = false;
        this._cellStates = new Array(this._prizePanelConfig.cells).fill(CollectorState.Available);
        // Call Updateable.
        this.onCollectorUpdated$();
    }

    private onEndGame$(): void {
        // Turn off one to go loops.
        if (this._oneToGo && !this._win) {
            this._cellViews[this._cellViews.length - 1].state.setAnimation(1, 'oneToGoStop', false);
        }
    }

    private addListeners(): void {
        gameEventEmitter.on('NEW_GAME', this.onNewGame$.bind(this));
        events.GAME.COMPLETE.addListener(this.onEndGame$.bind(this));
        events.CABINET.STAKE_CHANGE.addListener(() => {
            this.updatePrizeValue$();
            this.onNewGame$();
        });
        gameModel.bindToValue(
            this,
            () => gameModel.collectorStates,
            () => this.onCollectorUpdated$
        );
    }
}
